<template>
  <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <div class="flex items-center">
          <router-link to="/" class="flex items-center gap-3">
            <img
              :src="systemStore.theme === 'dark' ? '/radsuite-logo-dark.png' : '/radsuite-logo.png'"
              alt="RADSuite"
              class="h-8 w-auto"
            />
            <span class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Suite</span>
          </router-link>
          
        </div>
        
        <div class="flex items-center space-x-4">
          <!-- Theme Toggle Button -->
          <button
            @click="systemStore.toggleTheme()"
            class="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none"
            :title="systemStore.theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
          >
            <SunIcon v-if="systemStore.theme === 'dark'" class="w-6 h-6" />
            <MoonIcon v-else class="w-6 h-6" />
          </button>

          <!-- Dashboard Button for authenticated users -->
          <router-link
            v-if="authStore.isAuthenticated"
            to="/dashboard"
            class="text-sm bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            Dashboard
          </router-link>

          <!-- Sign In Button for non-authenticated users -->
          <router-link
            v-else
            to="/signin"
            class="text-sm bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            Sign In
          </router-link>
          
          <!-- User Menu for authenticated users -->
          <div v-if="authStore.isAuthenticated" class="relative user-menu-container">
            <button
              @click.stop="showUserMenu = !showUserMenu"
              class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
            >
              <img
                v-if="authStore.user?.avatarUrl"
                :src="authStore.user.avatarUrl"
                :alt="authStore.user.displayName"
                class="w-8 h-8 rounded-full"
              />
              <div v-else class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span class="text-sm font-medium text-gray-600">
                  {{ authStore.user?.displayName?.[0] || authStore.user?.email?.[0] || 'U' }}
                </span>
              </div>
              <span>{{ authStore.user?.displayName || authStore.user?.email }}</span>
              <svg 
                class="w-4 h-4 transition-transform duration-200" 
                :class="{ 'rotate-180': showUserMenu }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <!-- Dropdown menu with proper positioning and transitions -->
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showUserMenu"
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 origin-top-right border border-gray-200 dark:border-gray-700"
                @click.stop
              >
                <div class="py-1">
                  <button
                    @click="signOut"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSystemStore } from '@/stores/system'
import { SunIcon, MoonIcon } from '@heroicons/vue/24/outline'

const authStore = useAuthStore()
const systemStore = useSystemStore()
const router = useRouter()
const showUserMenu = ref(false)

async function signOut() {
  console.log('NavigationHeader: SignOut button clicked')
  try {
    await authStore.signOut()
    console.log('NavigationHeader: SignOut successful')
    showUserMenu.value = false
  } catch (error) {
    console.error('NavigationHeader: SignOut failed:', error)
  }
}

// Close menus when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element
  if (!target.closest('.user-menu-container')) {
    showUserMenu.value = false
  }
}

// Close menu when pressing Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showUserMenu.value = false
  }
}

// Add event listeners
document.addEventListener('click', handleClickOutside)
document.addEventListener('keydown', handleEscapeKey)

// Clean up event listeners when component is unmounted
// Note: In a real Vue component, we would use onUnmounted lifecycle hook
// For now, we'll handle this in the parent components that use this header
</script>